import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Shield, Loader2, AlertTriangle, CheckCircle2, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact account-security card. With email-OTP 2FA there's no QR or backup
 * codes — enabling is a single click for any user. The root admin account is
 * always treated as enabled+mandatory.
 */
export default function TwoFASettingsCard({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const auth = { Authorization: `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/2fa/status`, { headers: auth });
      setStatus(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load 2FA status.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const enable = async () => {
    setError(''); setSuccess(''); setSubmitting(true);
    try {
      await axios.post(`${API}/auth/2fa/enable`, {}, { headers: auth });
      setSuccess('Email two-factor authentication is now enabled. You will be asked for a 6-digit code at every sign-in.');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not enable 2FA.');
    } finally {
      setSubmitting(false);
    }
  };

  const disable = async () => {
    if (!window.confirm('Disable email 2FA? Your account will only be protected by your password.')) return;
    setError(''); setSuccess(''); setSubmitting(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, {}, { headers: auth });
      setSuccess('Email 2FA disabled.');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not disable 2FA.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="twofa-settings-card"
        className="grid-border bg-white p-4 flex items-center gap-2 text-sm text-slate-500"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading security status…
      </div>
    );
  }

  const enabled = !!status?.enabled;
  const mandatory = !!status?.mandatory;

  return (
    <div data-testid="twofa-settings-card" className="grid-border bg-white p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          {enabled ? (
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
          ) : (
            <Shield className="w-5 h-5 text-slate-400" />
          )}
          <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Two-Factor Authentication
          </h3>
          {enabled ? (
            <span
              data-testid="twofa-status-enabled"
              className="text-[10px] uppercase font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded-sm"
            >
              Enabled
            </span>
          ) : (
            <span
              data-testid="twofa-status-disabled"
              className="text-[10px] uppercase font-bold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm"
            >
              Disabled
            </span>
          )}
          {mandatory && (
            <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">
              Mandatory
            </span>
          )}
        </div>
        <span className="text-xs text-slate-500 inline-flex items-center gap-1">
          <Mail className="w-3.5 h-3.5" /> Codes sent to {status?.delivery_to || 'your email'}
        </span>
      </div>

      {error && (
        <div
          data-testid="twofa-settings-error"
          className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm mb-3 inline-flex items-center gap-1.5"
        >
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
        </div>
      )}
      {success && (
        <div
          data-testid="twofa-settings-success"
          className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-sm mb-3 inline-flex items-center gap-1.5"
        >
          <CheckCircle2 className="w-3.5 h-3.5" /> {success}
        </div>
      )}

      <div className="text-sm text-slate-700 space-y-3">
        {enabled ? (
          <>
            <p>
              Every sign-in to your account requires a 6-digit code emailed to{' '}
              <strong>{status?.delivery_to}</strong>. No authenticator app needed.
            </p>
            {mandatory ? (
              <p className="text-xs text-amber-700">
                Email 2FA is mandatory on the root administrator account and cannot be disabled.
              </p>
            ) : (
              <button
                type="button"
                onClick={disable}
                disabled={submitting}
                data-testid="twofa-disable-btn"
                className="text-xs text-red-700 hover:text-red-900 underline disabled:opacity-50"
              >
                Disable email 2FA…
              </button>
            )}
          </>
        ) : (
          <>
            <p>
              Add a second factor. After enabling, we'll email a fresh 6-digit code at every sign-in —
              no authenticator app required.
            </p>
            <button
              type="button"
              onClick={enable}
              disabled={submitting}
              data-testid="twofa-enable-btn"
              className="px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-sm hover:bg-black disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Enable email 2FA
            </button>
          </>
        )}
      </div>
    </div>
  );
}
